import React, { useMemo, useLayoutEffect, useRef } from 'react';
import sanitizeHtml from 'sanitize-html';
import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import { froalaStyle, textBoxStyle } from 'fever-froala-editor/src/lib/cssFroala';
import { stylePropType } from 'utils/propTypes';
import generatePictureSourceData from 'client/utils/generatePictureSourceData';
import useGetFontFamilyByI18nLang from 'hooks/useGetFontFamilyByI18nLang';
import { IS_EDITOR_MODE } from 'config/envVariable';

function TextBoxClient({
    onClick = () => {},
    style,
    content,
    ignoreContentPadding = false,
    shouldUseCompressedImg = false
}) {
    const fontFamily = useGetFontFamilyByI18nLang({ isClient: !IS_EDITOR_MODE });
    const fontFamilyBaseOnLang = `
        font-family: "${fontFamily}";
    `;
    const divRef = useRef();
    const __html = useMemo(() => {
        let processes = [sanitizeHtml, setImageLazyloadStyle];
        if (shouldUseCompressedImg) {
            processes.push(replaceImageWithCompressed);
        }
        processes.push(replaceEmptyLink);

        return processCallbacks(content, processes);
    }, [shouldUseCompressedImg, content]);

    useLayoutEffect(() => {
        if (divRef.current) {
            const imgs = divRef.current.querySelectorAll('img');
            imgs.forEach(img => {
                img.onload = () => {
                    img.classList.remove('lazyload');
                };
            });
        }
    }, [__html]);

    return (
        <div css={css('overflow: hidden; width: 100%;', style)} onClick={onClick}>
            <div
                css={css(
                    displayStyle({
                        ignoreContentPadding,
                        customFontFamilyStyle: fontFamilyBaseOnLang
                    })
                )}
                dangerouslySetInnerHTML={{ __html }}
                ref={divRef}
            />
        </div>
    );
}

TextBoxClient.propTypes = {
    style: stylePropType,
    onClick: PropTypes.func,
    content: PropTypes.string,
    ignoreContentPadding: PropTypes.bool
};

export default TextBoxClient;

function processCallbacks(item, callbacks = []) {
    let result = item;
    callbacks.forEach(cb => (result = cb(item)));
    return result;
}

function setImageLazyloadStyle(content = '') {
    return content.replace(/<img.+?src="([^"]+)"[^>]*\/?>/gi, imgElementStr => {
        const dataWidth = (imgElementStr.match(/data-width="([\d.]+?)"/) ?? [])[1];
        const dataRatio = (imgElementStr.match(/data-ratio="([\d.]+?)"/) ?? [])[1];

        if (dataWidth && dataRatio) {
            const height = Math.round(dataWidth * dataRatio);
            imgElementStr = imgElementStr.replace(
                /<img /,
                `<img width="${dataWidth}" height="${height}" `
            );
        }

        return imgElementStr;
    });
}

function replaceImageWithCompressed(content = '') {
    return content.replace(/<img.+?src="([^"]+)"[^>]*\/?>/gi, (imgElementStr, src) => {
        const sourceDatas = generatePictureSourceData(src);

        if (sourceDatas.length > 0) {
            return `
                <picture>
                    ${sourceDatas
                        .map(
                            ({ srcSet, media }) => `<source srcset="${srcSet}" media="${media}" />`
                        )
                        .join('\n')}
                    ${imgElementStr}
                </picture>
            `;
        } else {
            return imgElementStr;
        }
    });
}

function replaceEmptyLink(content = '') {
    return content.replace(/href="#"/gi, () => {
        return 'href="javascript:void(0);"';
    });
}

const displayStyle = ({ ignoreContentPadding, customFontFamilyStyle }) => `
    ${froalaStyle}
    ${textBoxStyle}
    ${customFontFamilyStyle}
    ${ignoreContentPadding && `padding: 0;`}
    & a { text-decoration: none; }
    & iframe { max-width: 100%; vertical-align: top; }
    & img.lazyload {
        background: rgb(205, 205, 205) url('${placeholderImg}') no-repeat center;
    }
    `;

const placeholderImg = `data:image/svg+xml;base64,PCEtLSBCeSBTYW0gSGVyYmVydCAoQHNoZXJiKSwgZm9yIGV2ZXJ5b25lLiBNb3JlIEAgaHR0cDovL2dvby5nbC83QUp6YkwgLS0+Cjxzdmcgd2lkdGg9IjQ0IiBoZWlnaHQ9IjQ0IiB2aWV3Qm94PSIwIDAgNDQgNDQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgc3Ryb2tlPSIjZmZmIj4KICAgIDxnIGZpbGw9Im5vbmUiIGZpbGwtcnVsZT0iZXZlbm9kZCIgc3Ryb2tlLXdpZHRoPSIyIj4KICAgICAgICA8Y2lyY2xlIGN4PSIyMiIgY3k9IjIyIiByPSIxIj4KICAgICAgICAgICAgPGFuaW1hdGUgYXR0cmlidXRlTmFtZT0iciIKICAgICAgICAgICAgICAgIGJlZ2luPSIwcyIgZHVyPSIxLjhzIgogICAgICAgICAgICAgICAgdmFsdWVzPSIxOyAyMCIKICAgICAgICAgICAgICAgIGNhbGNNb2RlPSJzcGxpbmUiCiAgICAgICAgICAgICAgICBrZXlUaW1lcz0iMDsgMSIKICAgICAgICAgICAgICAgIGtleVNwbGluZXM9IjAuMTY1LCAwLjg0LCAwLjQ0LCAxIgogICAgICAgICAgICAgICAgcmVwZWF0Q291bnQ9ImluZGVmaW5pdGUiIC8+CiAgICAgICAgICAgIDxhbmltYXRlIGF0dHJpYnV0ZU5hbWU9InN0cm9rZS1vcGFjaXR5IgogICAgICAgICAgICAgICAgYmVnaW49IjBzIiBkdXI9IjEuOHMiCiAgICAgICAgICAgICAgICB2YWx1ZXM9IjE7IDAiCiAgICAgICAgICAgICAgICBjYWxjTW9kZT0ic3BsaW5lIgogICAgICAgICAgICAgICAga2V5VGltZXM9IjA7IDEiCiAgICAgICAgICAgICAgICBrZXlTcGxpbmVzPSIwLjMsIDAuNjEsIDAuMzU1LCAxIgogICAgICAgICAgICAgICAgcmVwZWF0Q291bnQ9ImluZGVmaW5pdGUiIC8+CiAgICAgICAgPC9jaXJjbGU+CiAgICAgICAgPGNpcmNsZSBjeD0iMjIiIGN5PSIyMiIgcj0iMSI+CiAgICAgICAgICAgIDxhbmltYXRlIGF0dHJpYnV0ZU5hbWU9InIiCiAgICAgICAgICAgICAgICBiZWdpbj0iLTAuOXMiIGR1cj0iMS44cyIKICAgICAgICAgICAgICAgIHZhbHVlcz0iMTsgMjAiCiAgICAgICAgICAgICAgICBjYWxjTW9kZT0ic3BsaW5lIgogICAgICAgICAgICAgICAga2V5VGltZXM9IjA7IDEiCiAgICAgICAgICAgICAgICBrZXlTcGxpbmVzPSIwLjE2NSwgMC44NCwgMC40NCwgMSIKICAgICAgICAgICAgICAgIHJlcGVhdENvdW50PSJpbmRlZmluaXRlIiAvPgogICAgICAgICAgICA8YW5pbWF0ZSBhdHRyaWJ1dGVOYW1lPSJzdHJva2Utb3BhY2l0eSIKICAgICAgICAgICAgICAgIGJlZ2luPSItMC45cyIgZHVyPSIxLjhzIgogICAgICAgICAgICAgICAgdmFsdWVzPSIxOyAwIgogICAgICAgICAgICAgICAgY2FsY01vZGU9InNwbGluZSIKICAgICAgICAgICAgICAgIGtleVRpbWVzPSIwOyAxIgogICAgICAgICAgICAgICAga2V5U3BsaW5lcz0iMC4zLCAwLjYxLCAwLjM1NSwgMSIKICAgICAgICAgICAgICAgIHJlcGVhdENvdW50PSJpbmRlZmluaXRlIiAvPgogICAgICAgIDwvY2lyY2xlPgogICAgPC9nPgo8L3N2Zz4=`;
